import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Artwork from '../components/Artwork.jsx'
import ErrorState from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import { CloseMark } from '../components/Marks.jsx'
import { ApiError, api } from '../lib/api.js'
import { plural } from '../lib/format.js'

/**
 * `/queue` — heard but not rated (QUEUE-001, parent PLAN §14).
 *
 * The list is derived on the server from listens minus ratings, so there is
 * no "refresh" anywhere on this page: rate something and it is gone the next
 * time the queue is read. The three quiet verbs are the §14 states — Later
 * snoozes a week, Dismiss holds until new listens arrive, Never is forever.
 * Rate is a link, not a state: the rating sheet lives on the album and song
 * pages, and the queue notices what it does.
 */

function itemKey(item) {
  return `${item.target_type}:${item.id}`
}

function target_path(item) {
  return item.target_type === 'release_group' ? `/album/${item.id}` : `/song/${item.id}`
}

function lastHeard(iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} ${plural(days, 'day')} ago`
  if (days < 365) {
    const months = Math.floor(days / 30)
    return `${months} ${plural(months, 'month')} ago`
  }
  const years = Math.floor(days / 365)
  return `${years} ${plural(years, 'year')} ago`
}

/**
 * `Album`, or `Song from {album}` — Josh's mock, verbatim. A song the catalog
 * only knows standalone drops the clause rather than inventing an album to
 * name (ruled 2026-08-28).
 */
function kindLabel(item) {
  if (item.target_type === 'release_group') return 'Album'
  return item.album ? `Song from ${item.album.title}` : 'Song'
}

function QueueRow({ item, error, onState }) {
  const isSong = item.target_type === 'recording'
  // A song wears its album's cover, and where there is no cover to wear it
  // draws the one its album draws — same seed string in, same colours and
  // pattern out — so a track and the record it came from stop looking like
  // two unrelated things.
  const cover = item.album ?? { title: item.title, artist: item.artist }
  return (
    // `queue-row` carries no styling above 520px. It is what the phone rule in
    // `app.css` scopes to, because `.queue-actions` alone would also catch the
    // import page's Cancel button.
    <li className="row queue-row">
      <Link to={target_path(item)} aria-hidden="true" tabIndex={-1}>
        <Artwork
          art={item.artwork_url ? { url: item.artwork_url } : null}
          title={cover.title}
          artist={cover.artist}
          size={52}
          shape={isSong ? 'circle' : 'square'}
        />
      </Link>
      {/* A div rather than a span, because the row's own alert is a <p> and a
          paragraph inside a span is not markup a browser will keep. */}
      <div className="row-main">
        <span className="row-title">
          <Link to={target_path(item)}>{item.title}</Link>
        </span>
        <span className="row-sub">
          {item.artist} · {kindLabel(item)} · {item.plays}{' '}
          {item.plays === 1 ? 'play' : 'plays'} · last heard {lastHeard(item.last_played_at)}
        </span>
        <InlineAlert>{error}</InlineAlert>
      </div>
      <span className="queue-actions">
        <Link className="btn btn-primary btn-sm" to={target_path(item)}>
          Rate
        </Link>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={() => onState(item, 'later')}
        >
          Later
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          aria-label={`Dismiss ${item.title}`}
          onClick={() => onState(item, 'dismissed')}
        >
          <CloseMark />
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          aria-label={`Never ask about ${item.title}`}
          onClick={() => onState(item, 'never')}
        >
          Never
        </button>
      </span>
    </li>
  )
}

export default function QueuePage() {
  const [state, setState] = useState({ loading: true, items: [], total: 0 })
  const [error, setError] = useState('')
  // One message per row, keyed the way the list keys itself.
  const [rowErrors, setRowErrors] = useState({})
  // The next page's failure belongs beside the control that asked for it, the
  // way the feed's does — not on a row and not over the whole page.
  const [moreError, setMoreError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (signal) => {
    try {
      const body = await api.queue({ signal })
      // An API that predates paging sends no `total`. Taking the length of
      // what it sent leaves this page behaving exactly as it does today —
      // nothing left to ask for, so no control — rather than reading a field
      // that may not be there (PLAN §Rollback).
      setState({ loading: false, items: body.items, total: body.total ?? body.items.length })
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err instanceof ApiError ? err.message : 'The queue did not load.')
      setState((current) => ({ ...current, loading: false }))
    }
  }, [])

  useEffect(() => {
    document.title = 'Unrated queue — Sonologue'
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  /**
   * The next page, asked for at `offset` — which every caller takes from the
   * length of the list it is holding, never from a number the server issued.
   * After an answer the queue is one shorter, so a server-side count would
   * point one row past where this list ends and that row would never be seen.
   */
  const more = useCallback(async (offset) => {
    setBusy(true)
    setMoreError('')
    try {
      const body = await api.queue({ offset })
      setState((current) => {
        // A row that moved down between the two requests arrives twice.
        // Dropped by the key the list already uses, so the duplicate cannot
        // collide with a React key and the row stays where it was.
        const held = new Set(current.items.map(itemKey))
        const items = [...current.items, ...body.items.filter((row) => !held.has(itemKey(row)))]
        return {
          ...current,
          items,
          // A page that comes back empty is the end of the queue whatever the
          // count says. Trusting the page is also what stops an emptied list
          // asking for the same nothing again and again.
          total: body.items.length === 0 ? items.length : (body.total ?? items.length),
        }
      })
    } catch (err) {
      // The control stays, so pressing it again is the retry.
      setMoreError(err instanceof ApiError ? err.message : 'More of the queue did not load.')
    } finally {
      setBusy(false)
    }
  }, [])

  // Answering the last row on a page is not the same as finishing the queue.
  // When it empties the list with items still waiting, ask for the page that
  // begins where this list now ends — which, having emptied it, is its start.
  useEffect(() => {
    if (state.loading || busy || moreError) return
    if (state.items.length === 0 && state.total > 0) more(state.items.length)
  }, [state, busy, moreError, more])

  async function setItemState(item, value) {
    const key = itemKey(item)
    setRowErrors((current) => ({ ...current, [key]: '' }))
    try {
      await api.setQueueState(item.target_type, item.id, value)
      setState((current) => ({
        ...current,
        items: current.items.filter(
          (row) => !(row.id === item.id && row.target_type === item.target_type),
        ),
        // The server's queue is one shorter too. Keeping the count in step is
        // what keeps the label honest and the next request's offset right.
        total: Math.max(0, current.total - 1),
      }))
      setRowErrors((current) => {
        const next = { ...current }
        delete next[key]
        return next
      })
    } catch (err) {
      // The row stays, because the server still has it waiting for a score.
      setRowErrors((current) => ({
        ...current,
        [key]: err instanceof ApiError ? err.message : 'Could not save that just now.',
      }))
    }
  }

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Unrated queue</h1>
        <Link className="btn btn-quiet btn-sm" to="/import">
          Import history
        </Link>
      </div>
      <p className="footnote mb-3">
        Music you&rsquo;ve listened to but haven&rsquo;t rated, from your imported history. Only
        you see this.
      </p>

      <div className="card">
        {error ? (
          <ErrorState title="The queue did not load" message={error} onRetry={() => load()} />
        ) : state.loading ? (
          <div className="card-pad">
            <p className="footnote mb-0">Loading…</p>
          </div>
        ) : state.total === 0 ? (
          <div className="empty-state">
            <h2>All caught up</h2>
            <p>
              Nothing you&rsquo;ve listened to is waiting for a score. New plays arrive when you
              <Link to="/import"> import your listening history</Link>.
            </p>
          </div>
        ) : (
          <>
            <ul className="hairline-list">
              {state.items.map((item) => (
                <QueueRow
                  key={itemKey(item)}
                  item={item}
                  error={rowErrors[itemKey(item)]}
                  onState={setItemState}
                />
              ))}
            </ul>
            {/* Outside every `.queue-row`, so the phone row guard neither
                measures this nor is affected by it. */}
            {state.items.length < state.total ? (
              <div className="card-pad">
                <button
                  type="button"
                  className="btn btn-tonal"
                  disabled={busy}
                  onClick={() => more(state.items.length)}
                >
                  Show {state.total - state.items.length} more
                </button>
                <InlineAlert>{moreError}</InlineAlert>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  )
}
